import React, { useCallback, useEffect, useState } from 'react';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import { useToast } from '../components/ui/Toast';
import type { AppraisalForm } from '../types/appraisal';
import type { MagicLinkToken } from '../types/magicLinkToken';
import {
  getAppraisal, signByEmployee, signByManager, AppraisalError,
} from '../services/appraisalService';
import { markTokenUsed, MagicLinkError } from '../services/magicLinkService';
import AppraisalSummaryView from '../components/epr/AppraisalSummaryView';
import SignaturePad from '../components/epr/SignaturePad';
import RecipientBanner from '../components/magic/RecipientBanner';

/**
 * Sign-off page. Renders the full appraisal summary, then a signature
 * area that supports BOTH typed-name and hand-drawn canvas signatures.
 * Employee mode also exposes a "disagreement comment" textarea that, if
 * filled, sets up the Appeal path per §G.2.
 */

type SignMode = 'typed' | 'drawn';

interface Props {
  token: MagicLinkToken;
}

const AppraisalSignMagicForm: React.FC<Props> = ({ token }) => {
  const toast = useToast();
  const [appraisal, setAppraisal] = useState<AppraisalForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const [typedName, setTypedName] = useState('');
  const [drawn, setDrawn] = useState<string | null>(null);
  const [signMode, setSignMode] = useState<SignMode>('drawn');
  const [confirmed, setConfirmed] = useState(false);
  const [disagree, setDisagree] = useState('');

  const mode: 'employee' | 'manager' = token.recipientRole === 'manager' ? 'manager' : 'employee';

  useEffect(() => {
    let alive = true;
    void getAppraisal(token.targetDocId)
      .then(a => { if (alive) setAppraisal(a); })
      .catch(e => { if (alive) setLoadError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token.targetDocId]);

  const expectedName = (token.recipientName ?? '').trim();
  const nameMatches  = typedName.trim().toLowerCase() === expectedName.toLowerCase()
                      && expectedName.length > 0;
  const hasDrawnSig  = drawn !== null && drawn.length > 100; // dataURL with actual ink
  const signatureReady = signMode === 'typed' ? nameMatches : hasDrawnSig;

  const canSubmit = signatureReady && confirmed && !busy && !!appraisal;

  const handleSign = useCallback(async () => {
    if (!appraisal) return;
    setBusy(true);
    try {
      const signatureData = signMode === 'drawn' && drawn
        ? drawn                                  // PNG dataURL
        : `typed:${typedName.trim()}`;
      const common = {
        appraisalId: appraisal.id,
        signedBy: token.recipientId,
        signedByName: token.recipientName ?? token.recipientId,
        signatureData,
        ipAddress:   await fetchClientIp(),
        userAgent:   navigator.userAgent,
        magicLinkTokenId: token.id,
      };
      if (mode === 'employee') {
        await signByEmployee({ ...common, disagreeComment: disagree.trim() || undefined });
      } else {
        await signByManager(common);
      }
      await markTokenUsed(token.id);
      setDone(true);
    } catch (e) {
      const err = e as AppraisalError | MagicLinkError | Error;
      toast.show(err.message || 'Sign-off failed', { icon: 'error' });
    } finally {
      setBusy(false);
    }
  }, [appraisal, token, mode, typedName, disagree, toast]);

  if (loading) return <CenterMessage tone="info" title="Loading appraisal..." />;
  if (loadError || !appraisal) {
    return (
      <CenterMessage
        tone="error"
        title="Cannot open appraisal"
        body={loadError ?? 'No appraisal exists for this link.'}
      />
    );
  }
  if (done) {
    return (
      <CenterMessage
        tone="success"
        title="Sign-off complete"
        body={
          mode === 'employee'
            ? (disagree.trim()
                ? 'Signature saved with your disagreement note. HR will facilitate the appeal process within 2 days.'
                : 'Your signature has been saved. The manager will sign next.')
            : 'Manager signature saved. The appraisal is now closed.'
        }
      />
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-stone-500">
              Step 5 — Appraisal Sign-off
            </div>
            <div className="text-base font-semibold text-stone-900">
              {mode === 'employee' ? 'Employee' : 'Manager'}: {token.recipientName ?? token.recipientEmail}
            </div>
          </div>
          <DisclosureBadge
            icon="draw"
            label={mode === 'employee' ? 'Employee signature' : 'Manager signature'}
            tone="info"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-32">
        {/* Identity banner — catches accidental email forwards. For the
            sign-off flow this is especially important; the masked email
            gives a bystander a visible reason to not click "Sign". */}
        <RecipientBanner
          recipientEmail={token.recipientEmail}
          recipientName={token.recipientName}
        />
        <AppraisalSummaryView appraisal={appraisal} />

        {mode === 'employee' && (
          <div className="rounded-xl border border-stone-200 bg-white p-5">
            <label className="block text-base font-medium text-stone-900">
              Disagreement note (optional)
            </label>
            <div className="text-sm text-stone-500 leading-snug mt-0.5 mb-3">
              If you don't fully agree with this result, write a brief reason.
              Filling this opens the Appeal path — HR will facilitate within
              2 days after sign-off.
            </div>
            <textarea
              value={disagree}
              onChange={e => setDisagree(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
              placeholder="I don't agree with ... because ..."
            />
          </div>
        )}

        <div className="rounded-xl border border-stone-200 bg-white p-5">
          <h3 className="text-base font-semibold text-stone-900 mb-3">Digital signature</h3>

          {/* Mode toggle */}
          <div className="inline-flex p-1 rounded-lg border border-stone-200 bg-stone-50 mb-4">
            <button
              type="button"
              onClick={() => setSignMode('drawn')}
              className={[
                'px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors',
                signMode === 'drawn'
                  ? 'bg-white text-stone-900 shadow-sm font-medium'
                  : 'text-stone-600 hover:text-stone-900',
              ].join(' ')}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>draw</span>
              Draw signature
            </button>
            <button
              type="button"
              onClick={() => setSignMode('typed')}
              className={[
                'px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors',
                signMode === 'typed'
                  ? 'bg-white text-stone-900 shadow-sm font-medium'
                  : 'text-stone-600 hover:text-stone-900',
              ].join(' ')}
            >
              <span className="material-symbols-rounded" style={{ fontSize: 16 }}>keyboard</span>
              Type name
            </button>
          </div>

          {signMode === 'drawn' ? (
            <>
              <p className="text-sm text-stone-600 mb-3">
                Use your mouse, finger, or stylus to sign below.
                Signing on behalf of: <span className="font-medium text-stone-900">{expectedName || '(name unavailable)'}</span>
              </p>
              <SignaturePad value={drawn ?? undefined} onChange={setDrawn} height={180} />
            </>
          ) : (
            <>
              <p className="text-sm text-stone-600 mb-3">
                Type your full name exactly as registered:
                <span className="ml-1 font-medium text-stone-900">{expectedName || '(name unavailable)'}</span>
              </p>
              <input
                type="text"
                value={typedName}
                onChange={e => setTypedName(e.target.value)}
                placeholder="Type your name here"
                className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
              />
              {!nameMatches && typedName && (
                <div className="mt-2 text-sm text-red-700">
                  Name does not yet match the registered name.
                </div>
              )}
            </>
          )}

          <label className="flex items-start gap-2 text-sm text-stone-700 mt-4 pt-4 border-t border-stone-200">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I confirm I have read this appraisal and
              {mode === 'employee'
                ? ' acknowledge that I discussed it with my manager.'
                : ' approve this review as the supervising manager.'}
            </span>
          </label>
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-stone-600 truncate">
            {signatureReady && confirmed
              ? 'Ready to sign'
              : signMode === 'drawn'
                ? 'Draw your signature and tick the confirmation box'
                : 'Type your name and tick the confirmation box'}
          </div>
          <Button
            variant="submit"
            icon="draw"
            disabled={!canSubmit}
            loading={busy}
            onClick={handleSign}
          >
            Sign
          </Button>
        </div>
      </footer>
    </div>
  );
};

async function fetchClientIp(): Promise<string> {
  // Best-effort. Server-side audit should use Firestore-server-resolved IP.
  // Phase-1: skip the external call and stamp a placeholder.
  return 'client-resolved';
}

export default AppraisalSignMagicForm;
